import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {
    addCommas,
    addFromColumn,
    toCurrency,
    sortArrayByKey,
    sortCurrency, parseCurrency
} from '../shared/functions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import ExpandableTableContainer from './ExpandableTableContainer';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import InventorySummaryIcon from 'material-ui/svg-icons/action/assignment';

/**
 * Settlement Reports class for displaying Category Audit Info
 *
 */
class SettlementReportCategory extends Component {
    constructor(props) {
        super(props);

        const list = this.processList(props.inventoryList);

        this.state = {
            categoryList: list,
            tableData: list,
            tableHeaders: [
                {label: 'Category', key: 'category'},
                {label: 'Remaining', key: 'totalRemaining'},
                {label: 'Counted', key: 'totalCounted'},
                {label: 'Shrink Variance', key: 'shrinkVariance'},
                {label: 'Overage Variance', key: 'overageVariance'},
                {label: 'Shrink Value', key: 'shrinkValue'},
                {label: 'Overage Value', key: 'overageValue'},
                {label: 'Sum of Shrink/Overage', key: 'sumValue'}
            ],
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
    }

    /* Data Processing ------------------------------ */

    /**
     * Process inventory list provided by Inventory Summary table
     *
     * @param inventoryList
     */
    processList(inventoryList) {
        // Get list of Categories
        const catList = this.assembleCategories(inventoryList);

        // Update each Category with totals from each inventory item
        for (let i = 0; i < inventoryList.length; i++) {
            const product = inventoryList[i];
            const catIdx = catList.findIndex(r => r.categoryId === product.categoryId);
            if (catIdx !== -1) {
                const catRow = catList[catIdx];

                let totalRemaining = catRow.totalRemaining + product.totalRemaining;
                let totalCounted = catRow.totalCounted + product.totalCounted;
                let shrinkVariance = catRow.shrinkVariance;
                let overageVariance = catRow.overageVariance;
                if (product.variance < 0) {
                    shrinkVariance += product.variance;
                } else if (product.variance >= 0) {
                    overageVariance += product.variance;
                }
                let shrinkValue = parseCurrency(catRow.shrinkValue) + parseCurrency(product.shrinkValue);
                let overageValue = parseCurrency(catRow.overageValue) + parseCurrency(product.overageValue);
                let sumValue = shrinkValue + overageValue;

                catList[catIdx].totalRemaining = totalRemaining;
                catList[catIdx].totalCounted = totalCounted;
                catList[catIdx].shrinkVariance = shrinkVariance;
                catList[catIdx].overageVariance = overageVariance;
                catList[catIdx].shrinkValue = toCurrency(shrinkValue);
                catList[catIdx].overageValue = toCurrency(overageValue);
                catList[catIdx].sumValue = toCurrency(sumValue);
            }
        }
        return catList;
    }

    /**
     * Assemble rows of Categories with initial columns
     *
     * @param list
     * @returns {*[]}
     */
    assembleCategories(list) {
        let catList = [];
        if (list && list.length > 0) {
            for (let i = 0; i < list.length; i++) {
                const item = list[i];
                const catIdx = catList.findIndex(r => r.categoryId === item.categoryId);
                if (catIdx === -1) {
                    catList.push({
                        category: item.category,
                        categoryId: item.categoryId,
                        totalCounted: 0,
                        totalRemaining: 0,
                        shrinkVariance: 0,
                        overageVariance: 0,
                        shrinkValue: '0.00',
                        overageValue: '0.00',
                        sumValue: '0.00'
                    });
                }
            }
            catList = sortArrayByKey(catList, 'category', 'ASC');
            const missingIdx = catList.findIndex(r => r.categoryId === '');
            if (missingIdx !== -1) {
                const missingCat = catList.splice(missingIdx, 1)[0];
                missingCat.category = '(No Category)';
                catList.splice(catList.length, 0, missingCat);
            }
        }
        return catList;
    }

    /**
     * Update current table data based on sort
     *
     * @param event
     */
    handleSortChange(event) {
        const tableData = getTableState(this.reactTable);
        if (tableData && tableData.length > 0) {
            this.setState({
                tableData: tableData
            });
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    render() {

        const {
            location,
            system,
            zeroDay
        } = this.props;

        const {
            categoryList,
            tableData,
            tableHeaders
        } = this.state;

        const defaultSort = setDefaultTableSort('productId');

        const totalsStyle = {display: 'block'};

        // ReactTable headers, columns and footer totals for Category Audit

        const inventoryColumns = [
            {
                Header: 'Products',
                columns: [
                    {
                        Header: 'Category',
                        accessor: 'category',
                        headerClassName: 'th-min-110',
                        style: tableStyles.tdMin,
                        Footer: (<span style={{...tableStyles.footer, ...totalsStyle}}>&nbsp;</span>)
                    }
                ],
                headerClassName: 'th-min-220',
                style: tableStyles.thGroupMin
            },
            {
                Header: 'Calculations',
                columns: [
                    {
                        Header: () => (<span>Total<br/>Remaining</span>),
                        accessor: 'totalRemaining',
                        Cell: row => (
                            <span>{addCommas(row.row.totalRemaining)}</span>
                        ),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {addCommas(addFromColumn(columnProps.data, 'totalRemaining'))}
                                </span>
                            )
                        })
                    },
                    {
                        Header: () => (<span>Total<br/>Counted</span>),
                        accessor: 'totalCounted',
                        Cell: row => (
                            <span>{addCommas(row.row.totalCounted)}</span>
                        ),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {addCommas(addFromColumn(columnProps.data, 'totalCounted'))}
                                </span>
                            )
                        })
                    },
                    {
                        Header: () => (<span>Shrink<br/>Variance</span>),
                        accessor: 'shrinkVariance',
                        Cell: row => (
                            <span>{addCommas(row.row.shrinkVariance)}</span>
                        ),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {addCommas(addFromColumn(columnProps.data, 'shrinkVariance'))}
                                </span>
                            )
                        })
                    },
                    {
                        Header: () => (<span>Overage<br/>Variance</span>),
                        accessor: 'overageVariance',
                        Cell: row => (
                            <span>{addCommas(row.row.overageVariance)}</span>
                        ),
                        filterable: false,
                        style: tableStyles.right,
                        headerStyle: tableStyles.thLast,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {addCommas(addFromColumn(columnProps.data, 'overageVariance'))}
                                </span>
                            )
                        })
                    }
                ]
            },
            {
                Header: 'Prices and Differences',
                columns: [
                    {
                        Header: () => (<span>Shrink<br/>Value</span>),
                        accessor: 'shrinkValue',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {toCurrency(addFromColumn(columnProps.data, 'shrinkValue'))}
                                </span>
                            )
                        })
                    },
                    {
                        Header: () => (<span>Overage<br/>Value</span>),
                        accessor: 'overageValue',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {toCurrency(addFromColumn(columnProps.data, 'overageValue'))}
                                </span>
                            )
                        })
                    },
                    {
                        Header: () => (<span>Sum of<br/>Shrink/Overage</span>),
                        accessor: 'sumValue',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (columnProps => {
                            return (
                                <span style={{...tableStyles.footer, ...totalsStyle}}>
                                    {toCurrency(addFromColumn(columnProps.data, 'sumValue'))}
                                </span>
                            )
                        })
                    }
                ]
            }
        ];

        return (
            <div>
                {categoryList && categoryList.length > 0 &&
                    <div className="reports-table-container">

                        {/* Table Header: Category Audit Info */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Category Audit Info */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={tableData}
                                        headers={tableHeaders}
                                        filename={'Settlement-Report-Category-Audit-' + system + '-' + location.id + '-' + zeroDay + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Category Audit Info
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <InventorySummaryIcon style={commonStyles.summaryIcon}/>
                                <span>Category Audit Info</span>
                            </h3>
                        </div>

                        {/* Table: Category Audit Info */}
                        <ExpandableTableContainer>
                            <ReactTable
                                key="table-category-audit"
                                data={categoryList}
                                columns={inventoryColumns}
                                defaultPageSize={1000}
                                defaultSorted={defaultSort}
                                filterable
                                defaultFilterMethod={filterCaseInsensitive}
                                onSortedChange={this.handleSortChange}
                                ref={(r) => this.reactTable = r}
                                showPagination={false}
                                resizable={false}
                                minRows={1}
                                className="-striped summary-table"
                            />
                        </ExpandableTableContainer>
                    </div>
                }
            </div>
        )
    }
}

SettlementReportCategory.propTypes = {
    addField: PropTypes.bool,
    inventoryList: PropTypes.array.isRequired,
    location: PropTypes.object.isRequired,
    system: PropTypes.string,
    zeroDay: PropTypes.string.isRequired
};

SettlementReportCategory.defaultProps = {
    addField: true,
    inventoryList: [],
    location: {
        dbaPriceLevel: null,
        id: null,
        name: null,
        priceLevel: null,
        productType: null
    },
    zeroDay: null
};

const enhance = compose(
    connect()
);

export default enhance(SettlementReportCategory);



// WEBPACK FOOTER //
// ./src/Fuse/components/SettlementReportCategory.js